import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Home } from "lucide-react";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Stripe redirects the browser here after a successful checkout. The URL
 * contains `?session_id=cs_test_…`. We poll `/api/payments/status/{id}` for
 * up to ~15s so the webhook (or the Stripe-polling fallback inside the
 * endpoint) has time to flip the `payment_status` to "paid".
 */
export default function PaymentSuccessView({ onNavigateHome }) {
  const [status, setStatus] = useState("checking"); // checking, paid, pending, error
  const [sessionId, setSessionId] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) {
      setStatus("error");
      return;
    }
    setSessionId(sid);

    let cancelled = false;
    const maxPolls = 8;        // ~16 s total
    const intervalMs = 2000;

    const poll = async (n) => {
      if (cancelled) return;
      try {
        const resp = await axios.get(`${API}/payments/status/${sid}`);
        const ps = String(resp.data?.payment_status || "").toLowerCase();
        if (ps === "paid") {
          setStatus("paid");
          return;
        }
        if (ps === "failed" || ps === "expired") {
          setStatus("error");
          return;
        }
        if (n >= maxPolls) {
          setStatus("pending");
          return;
        }
        setTimeout(() => poll(n + 1), intervalMs);
      } catch (err) {
        if (n >= maxPolls) {
          setStatus("error");
          return;
        }
        setTimeout(() => poll(n + 1), intervalMs);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-emerald-50 flex items-center justify-center px-4">
      <div
        data-testid="payment-success-view"
        className="max-w-lg w-full bg-white rounded-3xl shadow-xl border border-cyan-100 p-10 text-center"
      >
        {status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-cyan-500 mx-auto mb-5 animate-spin" />
            <h1 className="text-2xl font-bold text-[#1E3A5F] mb-2">
              Finalising your subscription…
            </h1>
            <p className="text-gray-600 text-sm">
              Stripe is confirming the payment with your bank. This usually
              takes a few seconds.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-5" data-testid="payment-success-icon" />
            <h1 className="text-3xl font-bold text-[#1E3A5F] mb-2">
              You're subscribed!
            </h1>
            <p className="text-gray-600 mb-8">
              Welcome to PriceValuator. Your plan is active and you can start
              running valuations right away.
            </p>
            <Button
              onClick={onNavigateHome}
              data-testid="payment-success-home-btn"
              className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-full px-6 py-3 shadow-lg"
            >
              <Home className="w-4 h-4 mr-2" />
              Go to PriceValuator
            </Button>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="w-14 h-14 text-amber-500 mx-auto mb-5 animate-spin" />
            <h1 className="text-2xl font-bold text-[#1E3A5F] mb-2">
              Payment is being processed
            </h1>
            <p className="text-gray-600 mb-6 text-sm">
              Stripe is still finalising this payment. You'll get a confirmation
              email as soon as it's complete — feel free to leave this page.
            </p>
            <p className="text-xs text-gray-400 mb-6 break-all">Session: {sessionId}</p>
            <Button variant="outline" onClick={onNavigateHome} className="rounded-full">
              Back to home
            </Button>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="text-2xl font-bold text-rose-700 mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-6 text-sm">
              We couldn't verify this payment. If you were charged, email
              support@pricevaluator.com — we'll sort it out right away.
            </p>
            <Button variant="outline" onClick={onNavigateHome} className="rounded-full">
              Back to home
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
